/**
 * SDK validation utilities
 *
 * These functions validate inputs before sending transactions to prevent
 * on-chain failures and provide helpful error messages.
 */
import { isAddress } from "viem"

// =============================================================================
// Errors
// =============================================================================

export class ValidationError extends Error {
	constructor(
		message: string,
		public readonly field: string,
	) {
		super(message)
		this.name = "ValidationError"
	}
}

// =============================================================================
// Data URI Validation
// =============================================================================

/** Supported data URI protocols */
export const SUPPORTED_PROTOCOLS = ["ipfs://", "ar://", "https://"] as const
export type SupportedProtocol = (typeof SUPPORTED_PROTOCOLS)[number]

/** CIDv0 starts with Qm and is 46 chars, CIDv1 starts with b (base32) + af (cidv1) */
const IPFS_CID_V0_REGEX = /^Qm[1-9A-HJ-NP-Za-km-z]{44}$/
// CIDv1 base32 format: starts with 'b' (base32 multibase prefix) + 'af' (cidv1 multicodec)
// Different codecs produce different prefixes: bafy (dag-pb), bafk (raw), bafybe (dag-cbor), etc.
const IPFS_CID_V1_REGEX = /^baf[a-z2-7]{56,}$/

/**
 * Validate an IPFS CID format
 */
export function isValidIpfsCid(cid: string): boolean {
	return IPFS_CID_V0_REGEX.test(cid) || IPFS_CID_V1_REGEX.test(cid)
}

/**
 * Validate a data URI has a supported protocol and valid format
 */
export function isValidDataUri(uri: string): boolean {
	if (!uri || typeof uri !== "string") {
		return false
	}

	// Check for supported protocols
	if (uri.startsWith("ipfs://")) {
		const cid = uri.slice(7)
		return isValidIpfsCid(cid)
	}

	if (uri.startsWith("ar://")) {
		// Arweave transaction IDs are 43 characters, base64url encoded
		const txId = uri.slice(5)
		return /^[a-zA-Z0-9_-]{43}$/.test(txId)
	}

	if (uri.startsWith("https://")) {
		// Basic URL validation
		try {
			new URL(uri)
			return true
		} catch {
			return false
		}
	}

	return false
}

/**
 * Create an IPFS data URI from a CID
 *
 * @example
 * ```ts
 * const uri = ipfsUri("QmXxx...")
 * // Returns: "ipfs://QmXxx..."
 * ```
 */
export function ipfsUri(cid: string): string {
	if (!cid) {
		throw new ValidationError("IPFS CID is required", "cid")
	}

	// If already has protocol, validate and return
	if (cid.startsWith("ipfs://")) {
		if (!isValidDataUri(cid)) {
			throw new ValidationError(`Invalid IPFS URI: ${cid}`, "cid")
		}
		return cid
	}

	// Validate raw CID
	if (!isValidIpfsCid(cid)) {
		throw new ValidationError(
			`Invalid IPFS CID format: ${cid}. Expected CIDv0 (Qm...) or CIDv1 (baf...)`,
			"cid",
		)
	}

	return `ipfs://${cid}`
}

/**
 * Create an Arweave data URI from a transaction ID
 *
 * @example
 * ```ts
 * const uri = arweaveUri("abc123...")
 * // Returns: "ar://abc123..."
 * ```
 */
export function arweaveUri(txId: string): string {
	if (!txId) {
		throw new ValidationError("Arweave transaction ID is required", "txId")
	}

	if (txId.startsWith("ar://")) {
		if (!isValidDataUri(txId)) {
			throw new ValidationError(`Invalid Arweave URI: ${txId}`, "txId")
		}
		return txId
	}

	if (!/^[a-zA-Z0-9_-]{43}$/.test(txId)) {
		throw new ValidationError(
			`Invalid Arweave transaction ID format: ${txId}. Expected 43 character base64url string`,
			"txId",
		)
	}

	return `ar://${txId}`
}

/**
 * Validate a data URI and throw a helpful error if invalid
 */
export function validateDataUri(uri: string, fieldName = "dataUri"): void {
	if (!uri) {
		throw new ValidationError(`${fieldName} is required`, fieldName)
	}

	if (!isValidDataUri(uri)) {
		const protocols = SUPPORTED_PROTOCOLS.join(", ")
		throw new ValidationError(
			`Invalid ${fieldName}: "${uri}". Must be a valid URI with protocol (${protocols})`,
			fieldName,
		)
	}
}

// =============================================================================
// Address Validation
// =============================================================================

/**
 * Validate an Ethereum address
 */
export function validateAddress(address: string, fieldName = "address"): void {
	if (!address) {
		throw new ValidationError(`${fieldName} is required`, fieldName)
	}

	if (!isAddress(address)) {
		throw new ValidationError(
			`Invalid ${fieldName}: "${address}". Must be a valid Ethereum address`,
			fieldName,
		)
	}

	if (address === "0x0000000000000000000000000000000000000000") {
		throw new ValidationError(`${fieldName} cannot be the zero address`, fieldName)
	}
}

// =============================================================================
// Amount Validation
// =============================================================================

/**
 * Validate a positive number
 */
export function validatePositiveNumber(value: number, fieldName: string): void {
	if (typeof value !== "number" || Number.isNaN(value)) {
		throw new ValidationError(`${fieldName} must be a number`, fieldName)
	}

	if (value <= 0) {
		throw new ValidationError(`${fieldName} must be greater than 0`, fieldName)
	}
}

/**
 * Validate a bucket number
 */
export function validateBucket(bucket: number, fieldName = "bucket"): void {
	if (typeof bucket !== "number" || Number.isNaN(bucket)) {
		throw new ValidationError(`${fieldName} must be a number`, fieldName)
	}

	if (!Number.isInteger(bucket)) {
		throw new ValidationError(`${fieldName} must be an integer`, fieldName)
	}

	if (bucket < 0) {
		throw new ValidationError(`${fieldName} must be >= 0`, fieldName)
	}

	if (bucket > 255) {
		throw new ValidationError(`${fieldName} must be <= 255 (uint8)`, fieldName)
	}
}
